import React, { useState, useEffect } from "react";
import { X, CreditCard, Loader2 } from "lucide-react";
import { hrService } from "../../services/hrService";
import { MONTHS } from "../../constants/hrConstants";
import { useHR } from "../../context/HRContext";
import toast from "react-hot-toast";

export function PaySalaryModal({ isOpen, onClose, employee, onPaymentSuccess }) {
  const { attendance, leaves } = useHR();
  const [loading, setLoading] = useState(false);
  const [paySetup, setPaySetup] = useState(null);
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-indexed

  const [month, setMonth] = useState(String(currentMonth));
  const [year, setYear] = useState(String(currentYear));
  const [workingDays, setWorkingDays] = useState(26);
  const [presentDays, setPresentDays] = useState(26);
  const [leaveDays, setLeaveDays] = useState(0);
  const [amount, setAmount] = useState(0);

  // Auto-calculate attendance details for the selected period
  useEffect(() => {
    if (!isOpen || !employee) return;

    // Filter attendance logs for this employee in the selected month & year
    const empLogs = attendance.filter(a => {
      if (a.employeeId !== employee.id) return false;
      const [aYear, aMonth] = a.date.split("-");
      return Number(aYear) === Number(year) && Number(aMonth) === Number(month);
    });

    // Helper to calculate total working days (excluding Sundays) in a month
    const getWorkingDaysCount = (y, m) => {
      const days = new Date(y, m, 0).getDate();
      let count = 0;
      for (let d = 1; d <= days; d++) {
        const dayOfWeek = new Date(y, m - 1, d).getDay();
        if (dayOfWeek !== 0) count++;
      }
      return count;
    };

    const calculatedWorking = getWorkingDaysCount(Number(year), Number(month));
    
    // 1. Absences count from attendance logs
    const absentCount = empLogs.filter(a => a.status === "absent").length;
    
    // 2. Approved leave days count from leaves collection
    let approvedLeaveCount = 0;
    const monthStart = new Date(Number(year), Number(month) - 1, 1);
    const monthEnd = new Date(Number(year), Number(month), 0);
    
    leaves.forEach(l => {
      if (l.employeeId !== employee.id || l.status !== "approved") return;
      
      const start = new Date(l.fromDate);
      const end = l.toDate ? new Date(l.toDate) : start;
      
      // Find overlap
      const overlapStart = new Date(Math.max(monthStart, start));
      const overlapEnd = new Date(Math.min(monthEnd, end));
      
      if (overlapStart <= overlapEnd) {
        // Count working days (excluding Sundays) within the overlap period
        for (let d = new Date(overlapStart); d <= overlapEnd; d.setDate(d.getDate() + 1)) {
          if (d.getDay() !== 0) { // Exclude Sundays
            approvedLeaveCount++;
          }
        }
      }
    });

    const totalLeaveDays = absentCount + approvedLeaveCount;

    setWorkingDays(calculatedWorking);
    setLeaveDays(totalLeaveDays);
    setPresentDays(Math.max(0, calculatedWorking - totalLeaveDays));
  }, [isOpen, employee, month, year, attendance, leaves]);

  // Fetch Pay Setup on open
  useEffect(() => {
    if (isOpen && employee) {
      setLoading(true);
      hrService.getPaySetupByEmployee(employee.id)
        .then((res) => {
          const setup = res?.data?.paySetup;
          setPaySetup(setup);
          
          // Compute baseline amount
          let baseSalary = 0;
          if (setup) {
            baseSalary = (setup.frequency === "yearly" || setup.grossSalary > 100000) ? (setup.grossSalary / 12) : setup.grossSalary;
          } else {
            baseSalary = Number(employee.salary || 0);
            if (baseSalary > 100000) baseSalary /= 12;
          }
          setAmount(baseSalary);
        })
        .catch((err) => {
          console.error("Error loading pay setup", err);
          setAmount(Number(employee.salary || 0));
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, employee]);

  // Recalculate salary dynamically when workingDays, presentDays or leaveDays change
  useEffect(() => {
    let baseSalary = 0;
    if (paySetup) {
      // Calculate earnings pro-rated, keep deductions flat
      const ratio = workingDays > 0 ? presentDays / workingDays : 1;
      const isYearly = paySetup.frequency === "yearly" || paySetup.grossSalary > 100000;
      const earnings = paySetup.components
        .filter((c) => c.type === "earning")
        .reduce((sum, c) => sum + (isYearly ? c.amount / 12 : c.amount), 0);
      const deductions = paySetup.components
        .filter((c) => c.type === "deduction")
        .reduce((sum, c) => sum + (isYearly ? c.amount / 12 : c.amount), 0);
      baseSalary = (earnings * ratio) - deductions;
    } else {
      const ratio = workingDays > 0 ? presentDays / workingDays : 1;
      let onboardingSalary = Number(employee?.salary || 0);
      if (onboardingSalary > 100000) onboardingSalary /= 12;
      baseSalary = onboardingSalary * ratio;
    }
    setAmount(Math.max(0, Math.round(baseSalary)));
  }, [workingDays, presentDays, leaveDays, paySetup, employee]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePay = async () => {
    setLoading(true);
    try {
      // 1. Load script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway script");
        setLoading(false);
        return;
      }

      // 2. Call backend to create Razorpay Order
      console.log("createPaymentOrder PAYLOAD:", {
        employeeId: employee.id,
        month: Number(month),
        year: Number(year),
        amount,
        workingDays,
        presentDays,
        leaveDays,
      });
      const res = await hrService.createPaymentOrder({
        employeeId: employee.id,
        month: Number(month),
        year: Number(year),
        amount,
        workingDays,
        presentDays,
        leaveDays,
      });

      const orderData = res.data?.data;
      if (!orderData) {
        toast.error("Failed to create payment order");
        setLoading(false);
        return;
      }

      // 3. Launch Razorpay Popup
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Venus Infra Services",
        description: `Disburse Salary - ${employee.name} (${MONTHS[Number(month) - 1]} ${year})`,
        order_id: orderData.orderId,
        handler: async function (response) {
          setLoading(true);
          try {
            // 4. Verify payment on backend
            await hrService.verifyPaymentSignature({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              workingDays,
              presentDays,
              leaveDays,
            });

            toast.success(`Salary of ₹${Number(amount).toLocaleString("en-IN")} disbursed successfully!`);
            onPaymentSuccess?.();
            onClose();
          } catch (verifyErr) {
            console.error("verifyPaymentSignature error details:", verifyErr.response?.data);
            toast.error(verifyErr.response?.data?.message || "Payment verification failed");
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: orderData.employee.name,
          email: orderData.employee.email,
          contact: orderData.employee.phone,
        },
        theme: {
          color: "#2b448f",
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          }
        },
        // Enable UPI with collect flow (shows QR Code + "Enter UPI ID" input)
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
          emi: true,
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("handlePay catch block:", err);
      console.error("handlePay response error:", err.response?.data);
      toast.error(err.response?.data?.message || "Failed to initiate payment");
      setLoading(false);
    }
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">Disburse Salary</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">{employee.name} · ID: {employee.id}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors animate-in">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Period selector */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-[10.5px] font-bold text-slate-400 uppercase tracking-wide">Month</label>
              <select 
                className="input mt-1 text-[13px]" 
                value={month} 
                onChange={(e) => setMonth(e.target.value)}
              >
                {MONTHS.map((m, idx) => (
                  <option key={idx} value={String(idx + 1)}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-[10.5px] font-bold text-slate-400 uppercase tracking-wide">Year</label>
              <select 
                className="input mt-1 text-[13px]" 
                value={year} 
                onChange={(e) => setYear(e.target.value)}
              >
                {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Attendance adjustments */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3">
            <h4 className="text-[11.5px] font-bold text-slate-600 uppercase tracking-wide">Attendance Adjustments</h4>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Total Days</label>
                <input 
                  type="number" 
                  className="input py-1 px-2.5 mt-0.5 h-8 text-[12px]" 
                  value={workingDays}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setWorkingDays(val);
                    setPresentDays(val - leaveDays);
                  }}
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Present Days</label>
                <input 
                  type="number" 
                  className="input py-1 px-2.5 mt-0.5 h-8 text-[12px]" 
                  value={presentDays}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setPresentDays(val);
                    setLeaveDays(workingDays - val);
                  }}
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-semibold">Leaves</label>
                <input 
                  type="number" 
                  className="input py-1 px-2.5 mt-0.5 h-8 text-[12px]" 
                  value={leaveDays}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setLeaveDays(val);
                    setPresentDays(workingDays - val);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Amount Display */}
          <div className="bg-gradient-to-br from-[#2b448f]/5 to-[#2b448f]/10 rounded-xl p-4 border border-[#2b448f]/20 flex justify-between items-center">
            <div>
              <span className="text-[11px] font-semibold text-slate-500">Calculated Net Payout</span>
              <p className="text-[22px] font-black text-[#2b448f] leading-none mt-1">
                ₹{amount.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="text-right text-[10px] text-slate-400">
              <p>Base Monthly: ₹{Number(paySetup ? ((paySetup.frequency === "yearly" || paySetup.grossSalary > 100000) ? paySetup.grossSalary / 12 : paySetup.grossSalary) : (Number(employee.salary || 0) > 100000 ? Number(employee.salary || 0) / 12 : employee.salary || 0)).toLocaleString("en-IN")}</p>
              <p className="mt-0.5">Frequency: Monthly</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <p className="text-[9.5px] text-slate-400 max-w-[200px] leading-tight">
            * Uses Razorpay Test Payment Gateway. Complete payment with test modes.
          </p>
          <div className="flex gap-2">
            <button 
              type="button"
              onClick={onClose} 
              disabled={loading}
              className="px-4 py-2 text-slate-500 text-[12px] font-semibold hover:bg-slate-100 rounded-lg transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePay}
              disabled={loading || amount <= 0}
              className="btn-primary flex items-center gap-1.5 px-4 py-2 text-[12px] shadow-sm shadow-[#2b448f]/10 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CreditCard className="w-3.5 h-3.5" />
              )}
              Pay Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
